import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import GuidedTour, { GuideButton } from '../primitives/GuidedTour';
import useTourState from '../primitives/useTourState';
import { getScreenerTourSteps, ensureHeatmapView } from './screenerTourSteps';

/**
 * ScreenerTourController — thin screener-specific wiring for the guided tour.
 *
 * Phases: idle → form → awaiting-results → results → idle
 * - Auto-starts the form phase on a user's first 2 desktop visits.
 * - The results phase begins when results first render (fresh run or restored
 *   cache); if the user finishes the form phase with no results yet, the tour
 *   quietly waits and resumes after their first run this session.
 * - Skip means "leave me alone": both phases are marked done and auto-start
 *   never fires again. The Guide button always relaunches the full tour.
 *
 * Desktop-only in v1 — mobile renders entirely different components with no
 * tour anchors, and mobile visits must not consume the desktop auto-start
 * window (countVisit is gated on !isMobile).
 */
const AUTO_START_DELAY_MS = 600;   // let the page layout settle after mount
const RESULTS_START_DELAY_MS = 800; // let the heatmap render + auto-scroll finish

export default function ScreenerTourController({ mode, hasResults, isMobile, formRef, onModeChange }) {
  const tourState = useTourState({
    sectionKey: 'screener',
    phases: ['form', 'results'],
    maxAutoVisits: 2,
    countVisit: !isMobile,
  });
  const { isPhaseDone, markPhaseDone, markAllDone, shouldAutoStart } = tourState;

  const [phase, setPhase] = useState('idle'); // 'idle' | 'form' | 'awaiting-results' | 'results'
  const autoStartedRef = useRef(false);

  // Always-fresh handle to the ACTIVE form's imperative API. Step prepare
  // closures can be created before a mode switch commits (mode-choice step);
  // this getter always dereferences the latest mode's form ref so demo
  // population lands on the newly mounted form, never a stale one.
  const latestFormRef = useRef(formRef);
  latestFormRef.current = formRef;
  const liveFormHandle = useRef({
    get current() {
      return latestFormRef.current?.current ?? null;
    },
  }).current;

  // liveFormHandle is a stable getter object — mode is the main re-generation
  // trigger (picking a mode on the choice step regenerates the step list).
  const { formSteps, resultsSteps } = useMemo(
    () => getScreenerTourSteps(mode, liveFormHandle, {
      onSelectMode: (key) => {
        if (onModeChange) onModeChange(key);
      },
    }),
    [mode, liveFormHandle, onModeChange]
  );

  // Auto-start on first visits (form phase; or results phase directly when the
  // form phase was completed on a prior visit and results are on screen).
  useEffect(() => {
    if (isMobile || autoStartedRef.current || phase !== 'idle') return;
    if (shouldAutoStart('form')) {
      autoStartedRef.current = true;
      const t = setTimeout(() => setPhase('form'), AUTO_START_DELAY_MS);
      return () => clearTimeout(t);
    }
    if (isPhaseDone('form') && shouldAutoStart('results') && hasResults) {
      autoStartedRef.current = true;
      const t = setTimeout(() => setPhase('results'), RESULTS_START_DELAY_MS);
      return () => clearTimeout(t);
    }
  }, [isMobile, phase, shouldAutoStart, isPhaseDone, hasResults]);

  // Waiting for the user's first run after the form phase ended without results.
  useEffect(() => {
    if (phase !== 'awaiting-results' || !hasResults) return;
    const t = setTimeout(() => setPhase('results'), RESULTS_START_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, hasResults]);

  const handleFormFinish = useCallback(() => {
    markPhaseDone('form');
    if (hasResults) {
      setPhase('results');
    } else {
      // Tour UI closes; the last form step told the user it resumes after a run.
      setPhase('awaiting-results');
    }
  }, [markPhaseDone, hasResults]);

  const handleResultsFinish = useCallback(() => {
    markPhaseDone('results');
    // The results phase may have switched to Detail View — restore the
    // screener's default heatmap view (no-op if already active).
    ensureHeatmapView();
    setPhase('idle');
  }, [markPhaseDone]);

  const handleSkip = useCallback(() => {
    markAllDone();
    if (phase === 'results') ensureHeatmapView();
    setPhase('idle');
  }, [markAllDone, phase]);

  const handleGuideClick = useCallback(() => {
    setPhase('form');
  }, []);

  if (isMobile) return null;

  const activeSteps = phase === 'form' ? formSteps : phase === 'results' ? resultsSteps : null;

  return (
    <>
      <GuideButton onClick={handleGuideClick} label="Guide" />
      {activeSteps && (
        <GuidedTour
          key={phase}
          steps={activeSteps}
          isActive
          onFinish={phase === 'form' ? handleFormFinish : handleResultsFinish}
          onSkip={handleSkip}
        />
      )}
    </>
  );
}
